// src/components/Dashboard/Dashboard.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useProgress } from '../../contexts/ProgressContext';
import { StoryService, StoryData } from '../../services/storyService';
import { getActiveDailyChallenge, getActiveWeeklyChallenge } from '../Storybook/challengeConfig';
import { RegistrationGate } from '../auth/RegistrationGate';
import JoinClassModal from '../Student/JoinClassModal';
import TodoList from '../Student/TodoList';
import CreateAssignmentModal from '../Teacher/CreateAssignmentModal';
import ClassCreationModal from '../Teacher/ClassCreationModal';
import { collection, query, where, getDocs, updateDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import TeacherDashboard from '../Teacher/TeacherDashboard';
import StudentDashboard from '../Student/StudentDashboard';
 
// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
    primary:            '#6a37d4',
    primaryLight:       '#ae8dff',
    primaryContainer:   '#ede8ff',
    secondary:          '#006947',
    secondaryContainer: '#d1fae5',
    tertiary:           '#6f5900',
    tertiaryContainer:  '#fed01b',
    surface:            '#f3f6ff',
    surfaceLow:         '#eaf1ff',
    surfaceHigh:        '#d6e3f9',
    surfaceHighest:     '#cfdef5',
    surfaceLowest:      '#ffffff',
    onBackground:       '#252f3c',
    onSurface:          '#252f3c',
    onSurfaceVariant:   '#525c6a',
    outlineVariant:     '#a4aebe',
    error:              '#b41340',
};
 
// ── Sidebar nav item ──────────────────────────────────────────────────────────
const NavItem: React.FC<{
    icon: string; label: string; path: string; badge?: string;
    active: boolean; onClick: () => void;
}> = ({ icon, label, path, badge, active, onClick }) => (
    <button
        onClick={onClick}
        style={{
            width: '100%', padding: '11px 16px',
            background: active ? C.primaryContainer : 'transparent',
            color: active ? C.primary : C.onSurfaceVariant,
            border: 'none', borderRadius: '14px',
            textAlign: 'left', fontSize: '0.875rem', fontWeight: '700',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            transition: 'all 0.15s',
        }}
        onMouseEnter={e => { if (!active) e.currentTarget.style.background = C.surfaceHigh; }}
        onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
        <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{icon}</span>
        <span style={{ flex: 1 }}>{label}</span>
        {badge && (
            <span style={{ background: C.tertiaryContainer, color: C.tertiary, padding: '2px 8px', borderRadius: '9999px', fontSize: '0.65rem', fontWeight: '800' }}>
                {badge}
            </span>
        )}
    </button>
);
 
// ── Stat card ─────────────────────────────────────────────────────────────────
const StatCard: React.FC<{
    icon: string; value: string | number; label: string;
    bg: string; color: string; note?: string;
}> = ({ icon, value, label, bg, color, note }) => (
    <div style={{ background: bg, borderRadius: '24px', padding: '28px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: '140px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ fontSize: '1.8rem', marginBottom: '8px' }}>{icon}</div>
        <div>
            <div style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '2.2rem', fontWeight: '900', color, lineHeight: 1 }}>{value}</div>
            <div style={{ fontSize: '0.8rem', fontWeight: '700', color, opacity: 0.75, marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
            {note && <div style={{ fontSize: '0.75rem', color, opacity: 0.6, marginTop: '4px' }}>{note}</div>}
        </div>
    </div>
);
 
// ── Main component ─────────────────────────────────────────────────────────────
const Dashboard: React.FC = () => {
    const { xp, level, streak, isChallengeCompleted, isGuestMode, getProgressSummary } = useProgress();
    const navigate = useNavigate();
    const loc = useLocation();
    const { currentUser } = useAuth();
    const username = currentUser?.displayName || 'Writer';
    const isTeacher = currentUser?.role === 'teacher';
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [stories, setStories] = useState<StoryData[]>([]);
    const [loading, setLoading] = useState(true);
    const [showChallengeGate, setShowChallengeGate] = useState(false);
    const [showJoinClassModal, setShowJoinClassModal] = useState(false);
    const [showAssignmentModal, setShowAssignmentModal] = useState(false);
    const [showClassModal, setShowClassModal] = useState(false);
    const [teacherTab, setTeacherTab] = useState<'overview' | 'classes'>('overview');
    const [teacherClasses, setTeacherClasses] = useState<{ id: string; name: string; joinCode: string; students: Record<string, boolean>; archived?: boolean }[]>([]);
    const [copiedClassId, setCopiedClassId] = useState<string | null>(null);
    const [recentSubmissions, setRecentSubmissions] = useState<{ storyId: string; studentId: string; title: string; studentName: string; className: string; classId: string; submittedAt: any }[]>([]);
    const [assignmentsDueCount, setAssignmentsDueCount] = useState(0);
    const [studentClasses, setStudentClasses] = useState<{ id: string; name: string; teacherName: string; joinCode: string }[]>([]);
    const [loadingStudentClasses, setLoadingStudentClasses] = useState(false);
    const hasLoadedRef = useRef(false);
    const isActive = (path: string) => loc.pathname === path;
 
    const dailyChallenge = getActiveDailyChallenge();
    const weeklyChallenge = getActiveWeeklyChallenge();
    const MAX_PUBLISHED_STORIES = 10;
    const MAX_PUBLISH_PER_MONTH = 3;
 
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
 
    useEffect(() => {
        if (!hasLoadedRef.current && currentUser) {
            hasLoadedRef.current = true;
            StoryService.getUserStories(currentUser.uid)
                .then(all => setStories(all.filter(s => !s.isChallenge)))
                .catch(console.error)
                .finally(() => setLoading(false));
        }
    }, [currentUser]);
 
    // Load teacher classes from Firestore
    const loadTeacherClasses = useCallback(async () => {
        if (!currentUser) return;
        try {
            const snap = await getDocs(query(collection(db, 'classes'), where('teacherId', '==', currentUser.uid)));
            const loaded = snap.docs.map(d => ({ id: d.id, ...d.data() } as any));
            setTeacherClasses(loaded);
        } catch (err) {
            console.error('Error loading teacher classes:', err);
        }
    }, [currentUser]);
 
    useEffect(() => {
        if (isTeacher && currentUser) loadTeacherClasses();
    }, [isTeacher, currentUser, loadTeacherClasses]);
 
    // Load real recent submissions + assignments due for teacher overview
    const loadTeacherOverview = useCallback(async () => {
        if (!currentUser) return;
        try {
            // Assignments due (active, not past due)
            const now = new Date();
            const aSnap = await getDocs(query(
                collection(db, 'assignments'),
                where('teacherId', '==', currentUser.uid),
                where('status', '==', 'active')
            ));
            const due = aSnap.docs.filter(d => {
                const dueDate = d.data().dueDate?.toDate?.();
                return dueDate && dueDate >= now;
            });
            setAssignmentsDueCount(due.length);
 
            // Recent published stories across teacher's classes
            const classSnap = await getDocs(query(
                collection(db, 'classes'),
                where('teacherId', '==', currentUser.uid)
            ));
            const classIds = classSnap.docs.map(d => d.id);
            const classNames: Record<string, string> = {};
            classSnap.docs.forEach(d => { classNames[d.id] = d.data().name; });
 
            if (classIds.length === 0) return;
 
            // Firestore 'in' queries max 10 items — chunk if needed
            const chunks: string[][] = [];
            for (let i = 0; i < classIds.length; i += 10) chunks.push(classIds.slice(i, i + 10));
 
            const allStories: any[] = [];
            for (const chunk of chunks) {
                const sSnap = await getDocs(query(
                    collection(db, 'stories'),
                    where('classId', 'in', chunk),
                    where('status', '==', 'published')
                ));
                sSnap.docs.forEach(d => allStories.push({ id: d.id, ...d.data() }));
            }
 
            // Sort by submittedAt desc, take top 5
            allStories.sort((a, b) => (b.submittedAt?.toMillis?.() ?? 0) - (a.submittedAt?.toMillis?.() ?? 0));
            const top = allStories.slice(0, 5);
 
            // Fetch student names
            const studentIds = Array.from(new Set(top.map((s: any) => s.userId).filter(Boolean)));
            const studentNames: Record<string, string> = {};
            await Promise.all(studentIds.map(async (uid) => {
                const uDoc = await getDoc(doc(db, 'users', uid));
                studentNames[uid] = uDoc.exists() ? uDoc.data().displayName || 'Student' : 'Student';
            }));
 
            setRecentSubmissions(top.map((s: any) => ({
                storyId:     s.id,
                studentId:   s.userId,
                title:       s.title || 'Untitled',
                studentName: studentNames[s.userId] || 'Student',
                className:   classNames[s.classId] || 'Class',
                classId:     s.classId,
                submittedAt: s.submittedAt,
            })));
        } catch (err) {
            console.error('Error loading teacher overview:', err);
        }
    }, [currentUser]);
 
    useEffect(() => {
        if (isTeacher && currentUser) loadTeacherOverview();
    }, [isTeacher, currentUser, loadTeacherOverview]);
 
    // Load classes the student is enrolled in
    const loadStudentClasses = useCallback(async () => {
        if (!currentUser || isTeacher) return;
        setLoadingStudentClasses(true);
        try {
            const snap = await getDocs(query(
                collection(db, 'classes'),
                where(`students.${currentUser.uid}`, '==', true)
            ));
            setStudentClasses(snap.docs.map(d => ({
                id:          d.id,
                name:        d.data().name || 'Unnamed Class',
                teacherName: d.data().teacherName || 'Teacher',
                joinCode:    d.data().joinCode || '',
            })));
        } catch (err) {
            console.error('Error loading student classes:', err);
        } finally {
            setLoadingStudentClasses(false);
        }
    }, [currentUser, isTeacher]);
 
    useEffect(() => {
        if (!isTeacher && currentUser) loadStudentClasses();
    }, [isTeacher, currentUser, loadStudentClasses]);
 
    // ── XP helpers ────────────────────────────────────────────────────────────
    const getXPForLevel = (l: number) => l <= 1 ? 0 : Math.floor(500 * Math.pow(1.4, l - 2));
    const getTotalXP = (l: number) => { let t = 0; for (let i = 2; i <= l; i++) t += getXPForLevel(i); return t; };
    const levelProgress = (() => {
        const cur = xp - getTotalXP(level);
        const needed = getTotalXP(level + 1) - getTotalXP(level);
        return { current: cur, needed, percent: Math.min(100, Math.max(0, (cur / needed) * 100)) };
    })();
    const xpUntilNext = levelProgress.needed - levelProgress.current;
 
    const getLevelTitle = (l: number) => {
        if (l >= 50) return '👑 Grand Master';
        if (l >= 40) return '🧙 Writing Wizard';
        if (l >= 30) return '📖 Epic Author';
        if (l >= 20) return '⭐ Story Star';
        if (l >= 10) return '📚 Chapter Champion';
        if (l >= 5)  return '⚔️ Word Warrior';
        if (l >= 2)  return '🌱 Story Sprout';
        return '📝 Story Starter';
    };
 
    const getPublishesThisMonth = () => {
        const first = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
        return stories.filter(s => {
            if (!s.publishedAt || !s.isPublished) return false;
            const d = s.publishedAt instanceof Object && 'toDate' in s.publishedAt ? s.publishedAt.toDate() : new Date(s.publishedAt);
            return d >= first;
        }).length;
    };
 
    const getRarityColor = (r: string) => ({ common: '#9CA3AF', uncommon: '#10B981', rare: '#3B82F6', epic: '#A855F7', glowing: '#F59E0B' }[r] ?? C.primary);
 
    const startChallenge = (type: 'daily' | 'weekly') => {
        if (isGuestMode) { setShowChallengeGate(true); return; }
        const ch = type === 'daily' ? dailyChallenge : weeklyChallenge;
        navigate('/regular-book-editor', { state: { isChallenge: true, challengeType: ch.type, challengePrompt: ch.prompt, challengeTitle: ch.title, challengeXP: ch.xpReward, challengeGoal: { type: 'words', target: ch.wordTarget } } });
    };
 
    const recentCards = [
        { id: 1, name: 'Captain Plot Twist', rarity: 'rare',     icon: '🎭', isNew: true },
        { id: 2, name: 'Mystery Detective',  rarity: 'uncommon', icon: '🔍', isNew: false },
        { id: 3, name: 'Space Explorer',     rarity: 'common',   icon: '🚀', isNew: false },
    ];
 
    const publishedCount = stories.filter(s => s.isPublished).length;
    const monthlyCount   = getPublishesThisMonth();
    const totalWords     = stories.reduce((sum, s) => sum + ((s as any).wordCount || 0), 0);
    const recentDrafts   = stories.filter(s => !s.isPublished).slice(0, 3);
 
    // ── Shared sidebar ────────────────────────────────────────────────────────
    const Sidebar = () => (
        <div style={{
            position: 'fixed', top: 55, left: 0, bottom: 0,
            width: '220px', background: C.surfaceLow,
            borderRadius: '0 24px 24px 0',
            padding: '24px 12px',
            display: 'flex', flexDirection: 'column', gap: '4px',
            zIndex: 40,
            overflowY: 'auto',
            boxShadow: '4px 0 16px rgba(0,0,0,0.04)',
        }}>
 
            <NavItem icon="🏠" label="Dashboard"      path="/dashboard"  active={isActive('/dashboard')}  onClick={() => navigate('/dashboard')} />
            {isTeacher ? (
                <>
                    <NavItem icon="👥" label="My Classes"      path="/teacher/classes"      active={isActive('/teacher/classes')}      onClick={() => navigate('/teacher/classes')} />
                    <NavItem icon="📋" label="Assignments"     path="/teacher/assignments"  active={isActive('/teacher/assignments')}  onClick={() => navigate('/teacher/assignments')} />
                    <NavItem icon="💬" label="Feedback"        path="/teacher/feedback"     active={isActive('/teacher/feedback')}     onClick={() => navigate('/teacher/feedback')} />
                </>
            ) : (
                <>
                    <NavItem icon="📚" label="My Stories" path="/my-stories" active={isActive('/my-stories')} onClick={() => navigate('/my-stories')} />
                    <NavItem icon="✏️" label="Write New"  path="/write"      active={isActive('/write') || isActive('/regular-book-editor')} onClick={() => navigate('/write')} />
                 
                    <NavItem icon="🃏" label="My Cards"   path="/my-cards"   active={isActive('/my-cards')}   onClick={() => navigate('/my-cards')} badge="Soon" />
                </>
            )}
            <div style={{ flex: 1 }} />
            <div style={{ padding: '12px', background: C.primaryContainer, borderRadius: '16px', marginTop: '16px' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: '800', color: C.primary, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px', fontFamily: "'Epilogue', sans-serif" }}>Level {level}</div>
                <div style={{ fontSize: '0.75rem', color: C.onSurfaceVariant, marginBottom: '8px' }}>{getLevelTitle(level)}</div>
                <div style={{ height: '6px', background: C.surfaceHigh, borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ width: `${levelProgress.percent}%`, height: '100%', background: `linear-gradient(90deg, ${C.primary}, ${C.primaryLight})`, borderRadius: '3px', transition: 'width 0.5s' }} />
                </div>
                <div style={{ fontSize: '0.7rem', color: C.onSurfaceVariant, marginTop: '4px' }}>{levelProgress.current.toLocaleString()} / {levelProgress.needed.toLocaleString()} XP</div>
            </div>
        </div>
    );
 
    // ── TEACHER LAYOUT ────────────────────────────────────────────────────────
    if (isTeacher) return <TeacherDashboard />;
 
    // ── STUDENT LAYOUT ────────────────────────────────────────────────────────
    return <StudentDashboard />;
};
 
export default Dashboard;


































